// ─────────────────────────────────────────────────────────────────────────────
// RexyPinScreen — the lock screen (Chunk 5, Step 6). The T-Rex forest is the lock
// screen; the grid console is the OS (spec §8). Forest renders ONLY here — once
// authenticated, App swaps to RexyHomepage and the forest never reappears.
//
// Drop-in replacement for <Login>: SAME props ({ onLogin, error }) so App's existing
// handleLogin / authError wiring is reused verbatim. This screen does NOT touch auth
// logic — it only collects a PIN and calls onLogin(pin). Token storage, the /auth
// call, and error handling all stay in App.handleLogin.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useState } from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../lib/rexyConsole';
import trexBg from '../assets/trexbig.png';

const MONO = REXY_FONT;
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'CLR', '0', 'GO'] as const;

export interface RexyPinScreenProps {
  onLogin: (pin: string) => void; // === App.handleLogin
  error?: string;
}

export default function RexyPinScreen({ onLogin, error }: RexyPinScreenProps) {
  const [pin, setPin] = useState('');

  const submit = useCallback(() => {
    if (pin.length > 0) onLogin(pin);
  }, [pin, onLogin]);

  const press = useCallback(
    (k: string) => {
      if (k === 'CLR') setPin('');
      else if (k === 'GO') submit();
      else setPin((p) => (p.length < 20 ? p + k : p));
    },
    [submit],
  );

  // Physical keyboard support — digits, Backspace, Enter.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') setPin((p) => (p.length < 20 ? p + e.key : p));
      else if (e.key === 'Backspace') setPin((p) => p.slice(0, -1));
      else if (e.key === 'Enter') submit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submit]);

  return (
    <div style={styles.root}>
      <div style={{ ...styles.forest, backgroundImage: `url(${trexBg})` }} />
      <div style={styles.scrim} />

      <div style={styles.panel}>
        {/* REXY logo / name above the pad */}
        <div style={styles.brand}>REXY</div>
        <div style={styles.tagline}>PERSONAL INTELLIGENCE OS</div>

        {/* Masked PIN display */}
        <div style={styles.display}>
          {pin.length === 0 ? (
            <span style={styles.placeholder}>ENTER PIN</span>
          ) : (
            <span style={styles.dots}>{'•'.repeat(pin.length)}</span>
          )}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* Keypad */}
        <div style={styles.pad}>
          {KEYS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => press(k)}
              style={{
                ...styles.key,
                ...(k === 'GO' ? styles.keyGo : null),
                ...(k === 'CLR' ? styles.keyClr : null),
              }}
            >
              {k === 'CLR' ? '⌫' : k}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    position: 'fixed',
    inset: 0,
    overflow: 'hidden',
    background: REXY_CONSOLE.bg,
    fontFamily: MONO,
    color: REXY_CONSOLE.primary,
  },
  forest: {
    position: 'absolute',
    inset: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  },
  // Dark scrim so the PIN UI reads over the forest (never inverts to light).
  scrim: {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(circle at 50% 42%, rgba(5,8,26,0.45), rgba(5,8,26,0.82))',
  },
  panel: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 300,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 14,
    padding: 28,
    borderRadius: 12,
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: 'rgba(8,8,24,0.55)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    boxShadow: '0 18px 50px rgba(0,0,0,0.6), inset 0 1px 0 rgba(0,191,255,0.10)',
  },
  brand: {
    fontFamily: MONO,
    fontSize: 30,
    fontWeight: 700,
    letterSpacing: 8,
    color: REXY_CONSOLE.primary,
    textShadow: '0 0 18px rgba(0,191,255,0.45)',
  },
  tagline: {
    fontFamily: MONO,
    fontSize: 9,
    letterSpacing: 3,
    color: REXY_CONSOLE.mutedText,
    marginTop: -6,
  },
  display: {
    width: '100%',
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${REXY_CONSOLE.divider}`,
    borderRadius: 6,
    background: REXY_CONSOLE.panel,
  },
  placeholder: {
    fontFamily: MONO,
    fontSize: 11,
    letterSpacing: 3,
    color: REXY_CONSOLE.dimText,
  },
  dots: {
    fontSize: 20,
    letterSpacing: 6,
    color: REXY_CONSOLE.primary,
  },
  error: {
    fontFamily: MONO,
    fontSize: 10,
    letterSpacing: 1,
    color: REXY_CONSOLE.critical,
    textAlign: 'center',
  },
  pad: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 10,
    width: '100%',
  },
  key: {
    fontFamily: MONO,
    fontSize: 18,
    letterSpacing: 1,
    color: REXY_CONSOLE.primary,
    background: REXY_CONSOLE.cardBg,
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    borderRadius: 8,
    padding: '12px 0',
    cursor: 'pointer',
    userSelect: 'none',
    boxShadow: 'inset 0 1px 0 rgba(0,191,255,0.08)',
  },
  keyClr: {
    color: REXY_CONSOLE.warning,
  },
  keyGo: {
    color: REXY_CONSOLE.bg,
    background: REXY_CONSOLE.primary,
    fontWeight: 700,
  },
};
